import { motion, useAnimation, useInView } from "framer-motion";
import {
  Children,
  createElement,
  ElementType,
  isValidElement,
  ReactNode,
  useEffect,
  useRef,
} from "react";

const wordVariants = {
  hidden: { opacity: 0, y: 24, filter: "blur(16px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)" },
};

type Token =
  | { type: "word"; key: string; content: ReactNode }
  | { type: "space"; key: string }
  | { type: "break"; key: string };

const tokenize = (children: ReactNode): Token[] => {
  const tokens: Token[] = [];

  Children.forEach(children, (child, childIdx) => {
    if (typeof child === "string") {
      child.split(/(\s+)/).forEach((part, partIdx) => {
        if (part === "") return;

        if (/^\s+$/.test(part)) {
          tokens.push({ type: "space", key: `s-${childIdx}-${partIdx}` });
        } else {
          tokens.push({
            type: "word",
            key: `w-${childIdx}-${partIdx}`,
            content: part,
          });
        }
      });
    } else if (isValidElement(child) && child.type === "br") {
      tokens.push({ type: "break", key: `br-${childIdx}` });
    } else {
      tokens.push({ type: "word", key: `n-${childIdx}`, content: child });
    }
  });

  return tokens;
};

/**
 * Like AnimateOnScroll's "blur-up", but reveals children word-by-word with
 * a staggered delay instead of blurring the whole block in at once.
 * Non-string children (e.g. a decorated <span> wrapping one word) are
 * treated as a single word; <br /> is preserved as a plain line break.
 */
export const AnimateWordsOnScroll = ({
  children,
  as = "span",
  className = "",
  wordClassName = "",
  duration = 0.5,
  stagger = 0.06,
  delay = 0.1,
  startIndex = 0,
}: {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  wordClassName?: string;
  duration?: number;
  stagger?: number;
  delay?: number;
  startIndex?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
    // eslint-disable-next-line
  }, [isInView]);

  const tokens = tokenize(children);
  let wordIndex = startIndex;

  return createElement(
    as,
    { ref, className },
    tokens.map((token) => {
      if (token.type === "space") return " ";
      if (token.type === "break")
        return createElement("br", { key: token.key });

      const idx = wordIndex;
      wordIndex += 1;

      return (
        <motion.span
          key={token.key}
          initial="hidden"
          animate={controls}
          variants={wordVariants}
          transition={{ duration, delay: delay + idx * stagger }}
          className={`inline-block ${wordClassName}`}
        >
          {token.content}
        </motion.span>
      );
    }),
  );
};
